import type { CoreError } from '@ingenico/api-sdk-core/common';
import { ALL_LNGS_NAMESPACES } from '@ingenico/util-i18n-config';
import { Level, showNotification } from '@ingenico/react-notifications';
import { useBusinessService } from '@ingenico/react-section-utils';
import { requestAndUpdateState } from '@ingenico/react-utils/functions';
import { useAsyncDataState } from '@ingenico/react-utils/hooks';
import { toString } from 'lodash-es';
import { useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DeviceManagementService } from '../domain/business/services/deviceManagement.service';
import type { DeviceLogsStopRecordingModal } from '../domain/models/deviceLogsModal.model';

export interface UseManageDeviceLogsModalActionsParams {
  readonly refetch?: () => void;
}

export interface UseManageDeviceLogsModalActionsReturn {
  readonly handleStopRecording: (logId: string) => void;
  readonly stopRecordingModal: DeviceLogsStopRecordingModal;
}
export function useManageDeviceLogsModalActions({
  refetch,
}: UseManageDeviceLogsModalActionsParams): UseManageDeviceLogsModalActionsReturn {
  const deviceManagementService = useBusinessService(DeviceManagementService);
  const { t } = useTranslation([ALL_LNGS_NAMESPACES.COMMON, ALL_LNGS_NAMESPACES.LOGICAL_DEVICE]);

  // ====================== stop recording ===========================
  const [isStopRecordingModalOpened, setIsStopRecordingModalOpened] = useState(false);
  const stagedLogId = useRef<string>();

  const [stopRecordingLogsState, setStopRecordingLogsState] = useAsyncDataState<unknown, CoreError>();

  const handleStopRecording = useCallback((logId: string) => {
    stagedLogId.current = logId;
    setIsStopRecordingModalOpened(true);
  }, []);

  const handleConfirm = useCallback(() => {
    requestAndUpdateState(
      deviceManagementService.stopRecordingLogs(toString(stagedLogId.current)),
      setStopRecordingLogsState,
      {
        onSuccess: () => {
          setIsStopRecordingModalOpened(false);
          showNotification({
            content: t('success.stoppedDeviceLogs', { ns: ALL_LNGS_NAMESPACES.LOGICAL_DEVICE }),
            level: Level.Success,
          });
          refetch?.();
        },
        onError: () => {
          showNotification({
            content: t('error.default', { ns: ALL_LNGS_NAMESPACES.COMMON }),
            level: Level.Error,
          });
        },
      },
    );
  }, [deviceManagementService, setStopRecordingLogsState, t, refetch]);

  const handleCloseStopRecording = useCallback(() => {
    setIsStopRecordingModalOpened(false);
  }, []);

  const stopRecordingModal = useMemo<DeviceLogsStopRecordingModal>(
    () => ({
      isOpen: isStopRecordingModalOpened,
      handleClose: handleCloseStopRecording,
      handleConfirm,
      isLoading: stopRecordingLogsState.isLoading,
    }),
    [handleCloseStopRecording, handleConfirm, isStopRecordingModalOpened, stopRecordingLogsState.isLoading],
  );

  // ================================================================

  return {
    handleStopRecording,
    stopRecordingModal,
  };
}
